/**
 * Scenario Configuration
 *
 * Centralized configuration for all target system + auth method combinations.
 * Used by both form rendering and payload transformation.
 * Configuration is derived from Agent Providers (getAgentProviders) as the source of truth.
 *
 * @module AuthenticationFields/scenarioConfig
 */

import { getScenarioConfigFromProviders } from './agentProvidersService';
import { DEFAULT_AGENT_PROVIDERS_CONFIG } from '../constants';

// ============================================================================
// Helper Functions (unchanged API for transformFormToPayload and consumers)
// ============================================================================

/**
 * Builds scenario key from target system and auth method
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {string} Scenario key
 */
export const buildScenarioKey = (targetSystem, authMethod) =>
  `${targetSystem}:${authMethod}`;

/**
 * Gets scenario configuration (targetSystemId, backendType, authScheme, fieldMapping).
 * Derived from Agent Providers JSON.
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {Object|null} Scenario config or null
 */
export const getScenarioConfig = (targetSystem, authMethod, context) => {
  const config = getScenarioConfigFromProviders(
    targetSystem,
    authMethod,
    context
  );

  if (!config) return null;

  return {
    targetSystemId: config.targetSystemId,
    backendType: config.backendType,
    authScheme: config.authScheme,
    fieldMapping: config.fieldMapping
  };
};

/**
 * Gets backend connection type for a scenario
 */
export const getBackendType = (targetSystem, authMethod, context) => {
  const config = getScenarioConfig(targetSystem, authMethod, context);

  return config?.backendType || 'BEDROCK';
};

/**
 * Gets auth scheme for a scenario
 */
export const getAuthScheme = (targetSystem, authMethod, context) => {
  const config = getScenarioConfig(targetSystem, authMethod, context);

  return config?.authScheme || 'ApiKey';
};

/**
 * Gets target system ID (UUID) for a scenario
 */
export const getTargetSystemId = (targetSystem, authMethod, context) => {
  const config = getScenarioConfig(targetSystem, authMethod, context);

  return config?.targetSystemId || '';
};

/**
 * Transforms form parameters to backend auth parameters.
 * Preserves null values (used in edit mode for unchanged sensitive fields).
 */
export const transformAuthParams = (
  targetSystem,
  authMethod,
  formParams,
  context
) => {
  const config = getScenarioConfig(targetSystem, authMethod, context);

  if (!config) return {};

  const result = {};

  Object.entries(config.fieldMapping).forEach(([frontendKey, backendKey]) => {
    const value = formParams[frontendKey];

    result[backendKey] = value !== undefined ? value : '';
  });

  return result;
};

/**
 * Gets all registered scenario keys (derived from providers)
 */
export const getRegisteredScenarios = () => {
  const keys = [];

  DEFAULT_AGENT_PROVIDERS_CONFIG.forEach((p) => {
    (p.authSchemeFormFields || []).forEach((m) => {
      keys.push(`${p.id}:${m.authScheme}`);
    });
  });

  return keys;
};

/**
 * Checks if a scenario is registered
 */
export const isValidScenario = (targetSystem, authMethod, context) => {
  return Boolean(
    getScenarioConfigFromProviders(targetSystem, authMethod, context)
  );
};

// Legacy export: sync builder for SCENARIO_CONFIG
export const buildScenarioConfig = () => {
  const keys = getRegisteredScenarios();
  const entries = [];

  for (const key of keys) {
    const [targetSystem, authMethod] = key.split(':');
    const config = getScenarioConfig(targetSystem, authMethod);

    if (config) {
      entries.push([key, config]);
    }
  }

  return Object.fromEntries(entries);
};

// SCENARIO_CONFIG built from default providers config
const SCENARIO_CONFIG = buildScenarioConfig();

export { SCENARIO_CONFIG };

export default SCENARIO_CONFIG;
